import { zodResolver } from "@hookform/resolvers/zod";
import {
  KdsButton,
  KdsHeader,
  KdsModal,
  KdsText,
  KdsTextField,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";
import { PermissionItem } from "@/api/__generated__/orval/auth";
import { PageTemplate } from "@/components/PageTemplate";
import { EcoScoreIndicatorsEnum } from "@/config/apiClient.types.ts";
import { useCatalog, useToasts } from "@/hooks";
import { useRolePermission } from "@/hooks/rolePermission/useRolePermission";
import {
  type EcoScoreFormInputs,
  ecoScoreSchema,
} from "./EcoScoringFormPage.schema";

const ecoScoreIndicators = Object.values(EcoScoreIndicatorsEnum);

export const EcoScoringFormPage = () => {
  const { t } = useTranslate();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const { useCreateOrUpdateEcoScore } = useCatalog();
  const { add } = useToasts();

  const { userHasPermission } = useRolePermission();

  const { mutateAsync: updateEcoScore, isPending } =
    useCreateOrUpdateEcoScore();

  const methods = useForm<EcoScoreFormInputs>({
    resolver: zodResolver(ecoScoreSchema),
    mode: "onChange",
    defaultValues: {
      catalogMediumId: "",
      climateChange: {
        value: 0,
        unit: t("oms.updateEcoScore.climateChange.unit"),
      },
      waterUse: {
        value: 0,
        unit: t("oms.updateEcoScore.waterUse.unit"),
      },
      photochemicalOzoneFormation: {
        value: 0,
        unit: t("oms.updateEcoScore.photochemicalOzoneFormation.unit"),
      },
    },
  });

  const translationFromIndicator = (indicator: EcoScoreIndicatorsEnum) => {
    switch (indicator) {
      case EcoScoreIndicatorsEnum.CLIMATE_CHANGE:
        return t("oms.updateEcoScore.climateChange.label");
      case EcoScoreIndicatorsEnum.WATER_USE:
        return t("oms.updateEcoScore.waterUse.label");
      case EcoScoreIndicatorsEnum.PHOTOCHEMICAL_OZONE_FORMATION:
        return t("oms.updateEcoScore.photochemicalOzoneFormation.label");
      default:
        return "";
    }
  };

  const { handleSubmit, reset, register, formState, trigger } = methods;

  const onSubmit = async (data: EcoScoreFormInputs) => {
    try {
      const res = await updateEcoScore({
        catalogMediumId: data.catalogMediumId,
        updateEcoScoreCommand: {
          waterUse: data.waterUse,
          climateChange: data.climateChange,
          photochemicalOzoneFormation: data.photochemicalOzoneFormation,
        },
      });

      if (res.status === 200 || res.status === 201) {
        add({
          state: "success",
          title: t("oms.updateEcoScore.callout.success"),
        });
        reset(data);
        setIsConfirmModalOpen(false);
      }
    } catch (error) {
      console.error(error);
      add({
        state: "error",
        title: t("oms.updateEcoScore.callout.error"),
      });
    }
  };

  const handleConfirm = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    setIsConfirmModalOpen(true);
  };

  if (!userHasPermission(PermissionItem.kazaaradminmanagement)) {
    return (
      <Navigate
        to={`/403?missing_perms=${PermissionItem.kazaaradminmanagement}`}
        replace
      />
    );
  }

  return (
    <PageTemplate>
      <PageTemplate.Header>
        <KdsHeader title={t("oms.updateEcoScore.title")} />
      </PageTemplate.Header>

      <PageTemplate.Content>
        <form
          onSubmit={(e) => void handleSubmit(onSubmit)(e)}
          className="flex h-fit w-full flex-col gap-6 rounded-2xl bg-white p-10 shadow"
        >
          {/* Product ID */}
          <KdsTextField
            label={t("oms.updateEcoScore.catalogMediumId.label")}
            placeholder={t("oms.updateEcoScore.catalogMediumId.placeholder")}
            dataCy="catalog-id"
            hasError={!!formState.errors.catalogMediumId}
            errorMessage={t("oms.updateEcoScore.catalogMediumId.error")}
            {...register("catalogMediumId")}
          />

          {/* Indicators */}
          {ecoScoreIndicators.map((field) => (
            <div key={field} className="flex gap-4">
              <KdsTextField
                label={translationFromIndicator(field)}
                type="number"
                dataCy={`${field}-value`}
                placeholder={t("oms.updateEcoScore.value")}
                {...register(`${field}.value`, {
                  valueAsNumber: true,
                  required: true,
                })}
                hasError={!!formState.errors[field]?.value}
                errorMessage={t("oms.updateEcoScore.errorValue")}
              />
              <KdsTextField
                label="‎"
                className="mt-10"
                type="text"
                hasError={!!formState.errors[field]?.unit}
                placeholder={t("oms.updateEcoScore.unit")}
                dataCy={`${field}-unit`}
                {...register(`${field}.unit`)}
                errorMessage={t("oms.updateEcoScore.errorUnit")}
              />
            </div>
          ))}

          {/* Buttons */}
          <div className="flex justify-end gap-4">
            <KdsButton
              variant="outlined"
              size="large"
              color="neutral"
              onPress={() => reset()}
            >
              {t("buttons.cancel")}
            </KdsButton>
            <KdsButton
              size="large"
              isLoading={isPending}
              onPress={() => void handleConfirm()}
            >
              {t("buttons.confirm")}
            </KdsButton>
          </div>

          {/* Confirmation modal */}
          <KdsModal
            isOpen={isConfirmModalOpen}
            onOpenChange={setIsConfirmModalOpen}
            size="sm"
            dialogProps={{
              dialogHeaderProps: {
                title: t("oms.updateEcoScore.confirm.title"),
              },
              content: (
                <KdsText>{t("oms.updateEcoScore.confirm.description")}</KdsText>
              ),
              dialogFooterProps: {
                buttonProps: [
                  {
                    children: t("buttons.cancel"),
                    variant: "ghost",
                    color: "neutral",
                    onPress: () => setIsConfirmModalOpen(false),
                  },
                  {
                    children: t("buttons.confirm"),
                    onPress: () => void handleSubmit(onSubmit)(),
                    isLoading: isPending,
                  },
                ],
              },
            }}
          />
        </form>
      </PageTemplate.Content>
    </PageTemplate>
  );
};
